import React, { useState, useEffect, Fragment } from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import orgamax from 'ampersand-app'
import debounce from 'lodash/debounce'

import toInvoicePosition from '../_helper/toInvoicePosition'

import lang from '@/lang'

import CredentialForm from './credential-form'
import ListHeader from '@/modules/base/components/list/header/list-header-v2'
import ListQuickFilter from '@/modules/base/components/list/filter/list-quick-filter'
import List from '@/modules/base/components/list/list-v2'
import Search from '@/modules/base/components/list/search/search'

import IconAdd from '@assets/images/icons/add.svg'

import {
  fetchingTrackings,
  setAppId,
  lockTracking,
} from '@/redux/ducks/lists/requests/trackingList'

import { LIST_TYPES } from '@/redux/ducks/lists'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { withAsyncData } from '@/utils/withAsyncData'
import { useNavigate } from 'react-router'

const { TRACKING_LIST_TYPE } = LIST_TYPES

const useStyles = makeStyles(theme => ({
  root: {
    height: `calc(100vh - ${theme.menuHeight}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    height: '100%',
    '& .ag-theme-material .ag-row-selected': {
      backgroundColor: 'var(--ag-row-hover-color, #e9edf9)',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  search: {
    width: 250,
    height: 45,
    marginRight: 20,
    marginBottom: 6,
  },
}))

const TrackingList = props => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useDispatch()

  const listState = useSelector(state => state.lists)
  const {
    selectedNodes,
    error: errorCode,
    appId,
  } = listState[LIST_TYPES.TRACKING_LIST_TYPE]
  const [showCredentialInput, setShowCredentialInput] = useState(false)
  const [gridApi, setGridApi] = useState(null)

  useEffect(() => {
    if (errorCode?.app) {
      errorCode.app.forEach(error => {
        switch (error.code) {
          case 'uninstalled':
          case 'NOT_FOUND':
            toast.showToast({ type: 'error', message: lang.error.notActivated })
            debounce(() => navigate(`/app/all/show/${appId}`), 2000)()
            break

          case 'verify credentials':
          case 'invalid domain':
            setShowCredentialInput(true)
            break
        }
      })
    }
  }, [errorCode])

  useEffect(() => {
    dispatch(setAppId(props.appId))
  }, [])

  useEffect(() => {
    if (!showCredentialInput && appId) dispatch(fetchingTrackings(appId))
  }, [showCredentialInput, appId])

  const onListReady = api => setGridApi(api)

  const onSendClick = () => {
    const rows = gridApi.getSelectedRows()

    orgamax.cache = orgamax.cache || {}
    orgamax.cache.invoice = orgamax.cache.invoice || {}
    // orgamax.cache.invoice.customer = this.customer
    orgamax.cache.invoice.positions = rows.map(toInvoicePosition)
    orgamax.cache.onSaveFinished = () => rows.map(row => dispatch(lockTracking(appId, row.id)))

    if (rows.length) {
      navigate('/invoice/new')
    }
  }

  const headerButtons = [{
    label: lang.mite.createInvoice,
    icon: IconAdd,
    onClick: onSendClick,
    disabled: !selectedNodes.length,
  }]

  return (
    <div className={classes.root}>
      {showCredentialInput
        ? (
          <CredentialForm
            setShowCredentialInput={setShowCredentialInput}
            appId={appId}
          />
        )
        : (
          <Fragment>
            <ListHeader
              title={lang.mite.heading}
              buttons={headerButtons}
              gridApi={gridApi}
              hasSettingsButton={false}
              hasExportButton={false}
              listSettingName={TRACKING_LIST_TYPE}
            >
              <div className={classes.headerContainer}>
                <Search placeholder={lang.mite.filter.search}
                  customClass={classes.search}
                  listSettingName={TRACKING_LIST_TYPE} />
                <ListQuickFilter listSettingName={TRACKING_LIST_TYPE} />
              </div>
            </ListHeader>
            <div className={classes.list}>
              <List
                onListReady={onListReady}
                emptyText={lang.mite.noResult}
                listSettingName={TRACKING_LIST_TYPE}
              />
            </div>
          </Fragment>
        )}
    </div>
  )
}

const fetchData = async({ params }) => {
  const { appId } = params
  return Promise.resolve({ appId })
}

export default withAsyncData(TrackingList, 'TrackingList', fetchData)
